// API Service for UPLANDS FOOD MARKET
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Token management
const getToken = () => localStorage.getItem('auth_token');
const setToken = (token) => localStorage.setItem('auth_token', token);
const removeToken = () => localStorage.removeItem('auth_token');

// API Headers
const getHeaders = (includeAuth = true) => {
  const headers = {
    'Content-Type': 'application/json',
  };
  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return headers;
};

// Safe response reader - prevents "body stream already read" error
const safeReadResponse = async (response) => {
  try {
    // Clone the response to safely read the body
    const clonedResponse = response.clone();
    const text = await clonedResponse.text();
    
    if (!text || text.trim() === '') {
      return { data: null, text: '' };
    }
    
    try {
      const data = JSON.parse(text);
      return { data, text };
    } catch (e) {
      // Not JSON, return as text
      return { data: null, text };
    }
  } catch (error) {
    console.error('Error reading response:', error);
    return { data: null, text: '' };
  }
};

// Generic API call handler
const apiCall = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: getHeaders(options.auth !== false),
    });
    
    if (response.status === 401) {
      removeToken();
      window.location.reload();
      throw new Error('Session expired. Please login again.');
    }
    
    // Safely read the response body
    const { data, text } = await safeReadResponse(response);
    
    if (!response.ok) {
      throw new Error(data?.detail || data?.message || text || 'API request failed');
    }
    
    return data || { message: text || 'Success' };
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// ============== AUTH API ==============

export const authAPI = {
  login: async (email, password) => {
    const data = await apiCall('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      auth: false,
    });
    setToken(data.access_token);
    return data;
  },
  
  register: async (name, email, password, phone = null, role = 'customer', extraData = null) => {
    const data = await apiCall('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, phone, role, extra_data: extraData }),
      auth: false,
    });
    setToken(data.access_token);
    return data;
  },
  
  getMe: async () => {
    return apiCall('/api/auth/me');
  },
  
  logout: () => {
    removeToken();
  },
  
  isAuthenticated: () => {
    return !!getToken();
  },
};

// ============== PRODUCTS API ==============

export const productsAPI = {
  getAll: async (category = null, search = null) => {
    let endpoint = '/api/products';
    const params = new URLSearchParams();
    if (category && category !== 'all') params.append('category', category);
    if (search) params.append('search', search);
    if (params.toString()) endpoint += `?${params.toString()}`;
    return apiCall(endpoint, { auth: false });
  },
  
  getById: async (productId) => {
    return apiCall(`/api/products/${productId}`, { auth: false });
  },
  
  create: async (productData) => {
    return apiCall('/api/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  },
  
  update: async (productId, productData) => {
    return apiCall(`/api/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  },
  
  delete: async (productId) => {
    return apiCall(`/api/products/${productId}`, {
      method: 'DELETE',
    });
  },
  
  deleteAll: async () => {
    return apiCall('/api/products', {
      method: 'DELETE',
    });
  },
  
  importFromSource: async (sourceUrl = 'https://taste-of-ottawa-1f6e4ebc.base44.app', clearExisting = true) => {
    return apiCall('/api/admin/import-products', {
      method: 'POST',
      body: JSON.stringify({ source_url: sourceUrl, clear_existing: clearExisting }),
    });
  },
  
  loadFromBackup: async () => {
    return apiCall('/api/admin/load-backup', {
      method: 'POST',
    });
  },
  
  exportProducts: async () => {
    return apiCall('/api/admin/export-products');
  },
  
  restoreProducts: async (products, clearExisting = true) => {
    return apiCall('/api/admin/restore-products', {
      method: 'POST',
      body: JSON.stringify({ products, clear_existing: clearExisting }),
    });
  },
  
  toggleImageLock: async (productId, lock = true) => {
    return apiCall(`/api/products/${productId}/lock-image?lock=${lock}`, {
      method: 'PUT',
    });
  },
  
  lockAllImages: async () => {
    return apiCall('/api/products/lock-all-images', {
      method: 'POST',
    });
  },
  
  bulkUpload: async (images, category = 'spices', useAI = true) => {
    return apiCall('/api/products/bulk-upload', {
      method: 'POST',
      body: JSON.stringify({ images, category, use_ai: useAI }),
    });
  },
  
  analyzeImage: async (imageUrl) => {
    return apiCall('/api/products/analyze-image', {
      method: 'POST',
      body: JSON.stringify({ image_url: imageUrl }),
    });
  },
  
  getNeedsReview: async () => {
    return apiCall('/api/products/needs-review');
  },
};

// ============== ORDERS API ==============

export const ordersAPI = {
  create: async (orderData) => {
    return apiCall('/api/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },
  
  getAll: async () => {
    return apiCall('/api/orders');
  },
  
  getMyOrders: async () => {
    return apiCall('/api/orders/my-orders');
  },
  
  getById: async (orderId) => {
    return apiCall(`/api/orders/${orderId}`);
  },
  
  updateStatus: async (orderId, status) => {
    return apiCall(`/api/orders/${orderId}/status?status=${status}`, {
      method: 'PUT',
    });
  },
  
  confirmPayment: async (orderId, data) => {
    return apiCall(`/api/orders/${orderId}/confirm-payment`, {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId, status: data.status, notes: data.notes }),
    });
  },
  
  assignDriver: async (orderId, driverId) => {
    return apiCall(`/api/fulfillment/assign-driver?order_id=${orderId}&driver_id=${driverId}`, {
      method: 'POST',
    });
  },
};

// ============== USERS API ==============

export const usersAPI = {
  getAll: async () => {
    return apiCall('/api/users');
  },
  
  create: async (userData, role = 'customer') => {
    return apiCall(`/api/users?role=${role}`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },
  
  update: async (userId, data) => {
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) params.append(key, value);
    });
    return apiCall(`/api/users/${userId}?${params.toString()}`, {
      method: 'PUT',
    });
  },
  
  delete: async (userId) => {
    return apiCall(`/api/users/${userId}`, {
      method: 'DELETE',
    });
  },
};

// ============== DRIVERS API ==============

export const driversAPI = {
  create: async (driverData) => {
    return apiCall('/api/drivers', {
      method: 'POST',
      body: JSON.stringify(driverData),
      auth: false,
    });
  },
  
  getAll: async () => {
    return apiCall('/api/drivers');
  },
  
  updateStatus: async (driverId, status) => {
    return apiCall(`/api/drivers/${driverId}/status?status=${status}`, {
      method: 'PUT',
    });
  },
};

// ============== ROLES API ==============

export const rolesAPI = {
  getAll: async () => {
    return apiCall('/api/roles');
  },
  
  create: async (roleData) => {
    return apiCall('/api/roles', {
      method: 'POST',
      body: JSON.stringify(roleData),
    });
  },
  
  update: async (roleId, data) => {
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          params.append(key, JSON.stringify(value));
        } else {
          params.append(key, value);
        }
      }
    });
    return apiCall(`/api/roles/${roleId}?${params.toString()}`, {
      method: 'PUT',
    });
  },
  
  delete: async (roleId) => {
    return apiCall(`/api/roles/${roleId}`, {
      method: 'DELETE',
    });
  },
};

// ============== NOTIFICATIONS API ==============

export const notificationsAPI = {
  getAll: async () => {
    return apiCall('/api/notifications');
  },
  
  getUnreadCount: async () => {
    return apiCall('/api/notifications/unread-count');
  },
  
  markAsRead: async (notificationId) => {
    return apiCall(`/api/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
  },
  
  markAllAsRead: async () => {
    return apiCall('/api/notifications/read-all', {
      method: 'PUT',
    });
  },
  
  subscribeToPush: async (subscription) => {
    return apiCall('/api/notifications/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  },
  
  unsubscribeFromPush: async () => {
    return apiCall('/api/notifications/unsubscribe', {
      method: 'DELETE',
    });
  },
};

// ============== PROMOTIONS API ==============

export const promotionsAPI = {
  getAll: async () => {
    return apiCall('/api/promotions');
  },
  
  create: async (promoData) => {
    return apiCall('/api/promotions', {
      method: 'POST',
      body: JSON.stringify(promoData),
    });
  },
  
  send: async (promoId) => {
    return apiCall(`/api/promotions/${promoId}/send`, {
      method: 'POST',
    });
  },
  
  delete: async (promoId) => {
    return apiCall(`/api/promotions/${promoId}`, {
      method: 'DELETE',
    });
  },
};

// ============== BRANDING API ==============

export const brandingAPI = {
  get: async () => {
    // Public endpoint - no auth required
    try {
      const response = await fetch(`${API_URL}/api/branding`);
      const text = await response.text();
      if (!text) {
        return {}; // Return empty object for empty response
      }
      const data = JSON.parse(text);
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to fetch branding settings');
      }
      return data;
    } catch (error) {
      console.error('Branding API Error:', error);
      return {}; // Return empty object on error to use defaults
    }
  },
  
  update: async (updates) => {
    return apiCall('/api/branding', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },
  
  uploadImage: async (imageType, imageUrl) => {
    return apiCall(`/api/branding/upload?image_type=${imageType}&image_url=${encodeURIComponent(imageUrl)}`, {
      method: 'POST',
    });
  },
};

// ============== STRIPE API ==============

export const stripeAPI = {
  // Get public key (for checking if Stripe is enabled)
  getPublicKey: async () => {
    try {
      const response = await fetch(`${API_URL}/api/stripe/public-key`);
      const text = await response.text();
      if (!text) {
        return { publishable_key: null, enabled: false };
      }
      const data = JSON.parse(text);
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to check Stripe configuration');
      }
      return data;
    } catch (error) {
      console.error('Stripe API Error:', error);
      return { publishable_key: null, enabled: false };
    }
  },
  
  // Create checkout session
  createCheckout: async (orderId, originUrl) => {
    return apiCall('/api/stripe/checkout', {
      method: 'POST',
      body: JSON.stringify({
        order_id: orderId,
        origin_url: originUrl
      }),
    });
  },
  
  // Create Payment Intent (for Apple Pay / Google Pay)
  createPaymentIntent: async (orderId) => {
    return apiCall('/api/stripe/payment-intent', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId }),
    });
  },
  
  // Confirm Payment Intent
  confirmPaymentIntent: async (paymentIntentId, orderId) => {
    return apiCall('/api/stripe/payment-intent/confirm', {
      method: 'POST',
      body: JSON.stringify({ 
        payment_intent_id: paymentIntentId,
        order_id: orderId
      }),
    });
  },
  
  // Get checkout status
  getCheckoutStatus: async (sessionId) => {
    return apiCall(`/api/stripe/checkout/status/${sessionId}`);
  },
  
  // Admin: Get config (masked)
  getConfig: async () => {
    return apiCall('/api/stripe/config');
  },
  
  // Admin: Update config
  updateConfig: async (config) => {
    return apiCall('/api/stripe/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },
  
  // Admin: Delete config
  deleteConfig: async () => {
    return apiCall('/api/stripe/config', {
      method: 'DELETE',
    });
  },
};

// ============== DRIVER LOCATION API ==============

export const driverLocationAPI = {
  // Get driver location for an order
  getForOrder: async (orderId) => {
    return apiCall(`/api/driver-locations/order/${orderId}`);
  },
  
  // Update driver location (for drivers)
  update: async (locationData) => {
    return apiCall('/api/driver-locations', {
      method: 'POST',
      body: JSON.stringify(locationData),
    });
  },
  
  // Get current driver location
  getCurrent: async (driverId) => {
    return apiCall(`/api/driver-locations/${driverId}`);
  },
};

// ============== SHOPPING SESSIONS API ==============

export const shoppingSessionAPI = {
  // Get all active sessions (admin only)
  getActive: async () => {
    return apiCall('/api/shopping-sessions/active');
  },
  
  // Create or update a session
  upsert: async (sessionData, sessionId = null) => {
    if (sessionId) {
      return apiCall(`/api/shopping-sessions/${sessionId}`, {
        method: 'PUT',
        body: JSON.stringify(sessionData),
      });
    }
    return apiCall('/api/shopping-sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  },
  
  // End a session
  end: async (sessionId) => {
    return apiCall(`/api/shopping-sessions/${sessionId}/end`, {
      method: 'POST',
    });
  },
  
  // Get session stats (admin)
  getStats: async () => {
    return apiCall('/api/shopping-sessions/stats');
  },
};

// ============== CSV API ==============

export const csvAPI = {
  // Export data as CSV (admin only) with optional field selection
  export: async (dataType, fields = null) => {
    const token = getToken();
    let url = `${API_URL}/api/csv/export/${dataType}`;
    
    // Add fields query parameter if specified
    if (fields && fields.length > 0) {
      url += `?fields=${fields.join(',')}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Export failed');
    }
    
    return response.blob();
  },
  
  // Import data from CSV (admin only) with optional field mapping
  import: async (dataType, file, fields = null) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    
    // Add fields to form data if specified
    if (fields && fields.length > 0) {
      formData.append('fields', fields.join(','));
    }
    
    const response = await fetch(`${API_URL}/api/csv/import/${dataType}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      throw new Error(text || 'Import failed');
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Import failed');
    }
    
    return data;
  },
};

// ============== FILE UPLOAD API ==============

export const uploadAPI = {
  // Upload image file
  uploadImage: async (file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/api/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      throw new Error(text || 'Upload failed');
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Upload failed');
    }
    
    // Convert relative URL to full URL
    if (data.url && data.url.startsWith('/api/')) {
      data.url = `${API_URL}${data.url}`;
    }
    
    return data;
  },

  // Refine image with AI (background removal + enhancement)
  refineImage: async (imageBase64, refineType = 'both', options = null) => {
    const payload = {
      image_base64: imageBase64,
      refine_type: refineType
    };
    if (options) {
      payload.options = options;
    }
    return apiCall('/api/images/refine', {
      method: 'POST',
      body: JSON.stringify(payload),
      auth: false
    });
  },
};

// ============== LANGUAGE API ==============

export const languageAPI = {
  // Get supported languages
  getLanguages: async () => {
    return apiCall('/api/languages', { auth: false });
  },

  // Update user language preference
  updateLanguage: async (language) => {
    return apiCall('/api/users/language', {
      method: 'PUT',
      body: JSON.stringify({ language })
    });
  },

  // Get user language preference
  getLanguage: async () => {
    return apiCall('/api/users/language');
  }
};

// ============== GIFT CARDS API ==============

export const giftCardsAPI = {
  // Create checkout session for gift card purchase
  createCheckout: async (giftCardData) => {
    return apiCall('/api/gift-cards/checkout', {
      method: 'POST',
      body: JSON.stringify({
        ...giftCardData,
        origin_url: window.location.origin
      }),
      auth: false // Allow guest purchases
    });
  },

  // Check payment status
  checkStatus: async (sessionId) => {
    return apiCall(`/api/gift-cards/checkout/status/${sessionId}`, {
      auth: false
    });
  },

  // Redeem a gift card
  redeem: async (code) => {
    return apiCall('/api/gift-cards/redeem', {
      method: 'POST',
      body: JSON.stringify({ code })
    });
  },

  // Get user's gift card balance
  getBalance: async () => {
    return apiCall('/api/gift-cards/balance');
  }
};

// ============== PASSWORD RESET API ==============

export const passwordResetAPI = {
  // Request password reset
  requestReset: async (email) => {
    return apiCall('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
      auth: false
    });
  },

  // Confirm password reset
  confirmReset: async (token, newPassword) => {
    return apiCall('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
      auth: false
    });
  }
};

// ============== UTILITY ==============

export const seedDatabase = async () => {
  return apiCall('/api/seed', { method: 'POST', auth: false });
};

export const healthCheck = async () => {
  return apiCall('/api/health', { auth: false });
};

export default {
  auth: authAPI,
  products: productsAPI,
  orders: ordersAPI,
  users: usersAPI,
  drivers: driversAPI,
  roles: rolesAPI,
  notifications: notificationsAPI,
  promotions: promotionsAPI,
  branding: brandingAPI,
  csv: csvAPI,
  upload: uploadAPI,
  language: languageAPI,
  giftCards: giftCardsAPI,
  passwordReset: passwordResetAPI,
  seed: seedDatabase,
  health: healthCheck,
};
